import React, { useState, useMemo } from "react";
import { Sliders, RotateCcw, TrendingDown, ShieldAlert, Lock } from "lucide-react";

/**
 * PathwaySensitivitySlider — iter707d.
 *
 * What-if simulator embedded in the expanded DiseaseCard. The patient drags
 * per-pathway burden sliders DOWN to see how the activation score would
 * change IF they were to optimise that lifestyle / biology lever.
 *
 * 🛡 IMPORTANT — Strictly client-side. No data is sent to the server, no
 * sliders are persisted. We make this explicit in the UI (lock badge +
 * "симулатор" pill).
 *
 * The recomputation uses the exact same formula as `bdi_engine.py`:
 *   activation = Σ(burden_i × weight_i) / Σ(weight_i)
 */

const FACTOR_LABELS = {
  estrogen:     "Естрогенен товар",
  insulin:      "Инсулинов товар",
  igf1:         "IGF-1 товар",
  inflammation: "Възпаление",
  ecm:          "ECM / Колаген",
  density:      "Плътност",
  adiposity:    "Адипозитет",
  vitamin_d:    "Витамин D дефицит",
  hereditary:   "Наследственост (фикс.)",
  dna_repair:   "DNA repair",
};

const FACTOR_LABELS_EN = {
  estrogen:     "Estrogen burden",
  insulin:      "Insulin burden",
  igf1:         "IGF-1 burden",
  inflammation: "Inflammation",
  ecm:          "ECM / Collagen",
  density:      "Density",
  adiposity:    "Adiposity",
  vitamin_d:    "Vitamin D deficit",
  hereditary:   "Hereditary (fixed)",
  dna_repair:   "DNA repair",
};

const TX = {
  bg: {
    title:        "Симулатор · какво ако оптимизирам пътя",
    pill:         "СИМУЛАТОР",
    lockLine:     "Тези промени НЕ се запазват. Стойностите се преизчисляват само в браузъра Ви.",
    currentScore: "Текущ скор",
    simScore:     "Симулиран скор",
    band:         "Категория",
    band_low:     "Ниска",
    band_moderate:"Умерена",
    band_elevated:"Повишена",
    band_high:    "Силна",
    reset:        "Върни към реалните",
    hereditaryNote: "Наследственият принос е фиксиран — не може да се „оптимизира” чрез lifestyle.",
    delta:        "Промяна",
    transition:   "Преход",
    formulaTitle: "Формула",
    formula:      "activation = Σ(burden × weight) / Σ(weight)",
    note:         "Симулаторът е образователен — НЕ е клинично предписание.",
  },
  en: {
    title:        "Simulator · what if I optimise this pathway",
    pill:         "SIMULATOR",
    lockLine:     "These changes are NOT saved. Values are recomputed only in your browser.",
    currentScore: "Current score",
    simScore:     "Simulated score",
    band:         "Band",
    band_low:     "Low",
    band_moderate:"Moderate",
    band_elevated:"Elevated",
    band_high:    "High",
    reset:        "Reset to real values",
    hereditaryNote: "Hereditary contribution is fixed — it cannot be optimised through lifestyle.",
    delta:        "Δ",
    transition:   "Transition",
    formulaTitle: "Formula",
    formula:      "activation = Σ(burden × weight) / Σ(weight)",
    note:         "Educational simulator — NOT a clinical prescription.",
  },
};

const BAND_STYLES = {
  Low:      { bg: "bg-emerald-100 dark:bg-emerald-500/20", text: "text-emerald-700 dark:text-emerald-300" },
  Moderate: { bg: "bg-amber-100 dark:bg-amber-500/20",     text: "text-amber-700 dark:text-amber-300" },
  Elevated: { bg: "bg-orange-100 dark:bg-orange-500/20",   text: "text-orange-700 dark:text-orange-300" },
  High:     { bg: "bg-rose-100 dark:bg-rose-500/20",       text: "text-rose-700 dark:text-rose-300" },
};

function bandFor(act, thresholds) {
  if (act >= (thresholds?.High     ?? 0.75)) return "High";
  if (act >= (thresholds?.Elevated ?? 0.55)) return "Elevated";
  if (act >= (thresholds?.Moderate ?? 0.30)) return "Moderate";
  return "Low";
}

export default function PathwaySensitivitySlider({
  card,
  lang = "bg",
  isDark = false,
}) {
  const t = TX[lang] || TX.bg;
  const flabels = lang === "en" ? FACTOR_LABELS_EN : FACTOR_LABELS;
  const factors = (card.all_contributing_factors || card.top_contributing_factors || []);

  // Real burden snapshot — never mutated; reset reverts to this.
  const realBurdens = useMemo(() => {
    const m = {};
    factors.forEach((f) => { m[f.factor] = f.burden_0_1; });
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card.disease_id]);

  const [simBurdens, setSimBurdens] = useState(realBurdens);

  // Activation under simulated burdens (same formula as bdi_engine.py).
  const simActivation = useMemo(() => {
    let total = 0, wsum = 0;
    factors.forEach((f) => {
      const b = simBurdens[f.factor] ?? f.burden_0_1;
      total += b * f.weight;
      wsum  += f.weight;
    });
    return wsum > 0 ? total / wsum : 0;
  }, [simBurdens, factors]);

  const realActivation = card.activation_score_0_1 || 0;
  const thresholds = card.band_thresholds || { Moderate: 0.30, Elevated: 0.55, High: 0.75 };

  const realBand = card.association_level;
  const simBand  = bandFor(simActivation, thresholds);
  const realPct  = Math.round(realActivation * 100);
  const simPct   = Math.round(simActivation * 100);
  const deltaPct = simPct - realPct;
  const bandChanged = realBand !== simBand;

  const reset = () => setSimBurdens(realBurdens);
  const isDirty = factors.some((f) => Math.abs((simBurdens[f.factor] ?? f.burden_0_1) - f.burden_0_1) > 0.005);

  const setBurden = (factor, v) => {
    setSimBurdens((prev) => ({ ...prev, [factor]: Math.max(0, Math.min(1, v)) }));
  };

  const sB = BAND_STYLES[simBand]  || BAND_STYLES.Low;
  const rB = BAND_STYLES[realBand] || BAND_STYLES.Low;

  return (
    <div
      className={`rounded-xl border p-3 mt-3 ${isDark ? "bg-violet-500/5 border-violet-500/20" : "bg-violet-50/40 border-violet-200"}`}
      data-testid={`bdi-sensitivity-slider-${card.disease_id}`}
    >
      {/* Header + GDPR-friendly disclosure */}
      <div className="flex items-start gap-2 flex-wrap mb-2">
        <Sliders className="w-4 h-4 text-violet-500 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h6 className="font-bold text-xs leading-tight">{t.title}</h6>
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${isDark ? "bg-violet-500/20 text-violet-300" : "bg-violet-100 text-violet-700"}`}>
              {t.pill}
            </span>
          </div>
          <div className={`mt-1 flex items-center gap-1.5 text-[10px] ${isDark ? "text-gray-400" : "text-gray-600"}`}>
            <Lock className="w-3 h-3" />
            <span>{t.lockLine}</span>
          </div>
        </div>
      </div>

      {/* Score comparison */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className={`rounded-lg p-2 text-center ${isDark ? "bg-black/30" : "bg-white"}`}>
          <div className="text-[9px] uppercase tracking-wider opacity-60">{t.currentScore}</div>
          <div className="font-bold text-lg leading-none mt-1">{realPct}%</div>
          <span className={`inline-block mt-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full ${rB.bg} ${rB.text}`}>
            {t[`band_${(realBand || "low").toLowerCase()}`] || realBand}
          </span>
        </div>
        <div className={`rounded-lg p-2 text-center ${isDark ? "bg-violet-500/15" : "bg-violet-100/60"}`}>
          <div className="text-[9px] uppercase tracking-wider opacity-60">{t.simScore}</div>
          <div className="font-bold text-lg leading-none mt-1 flex items-center justify-center gap-1">
            {simPct}%
            {deltaPct !== 0 && (
              <span className={`text-[10px] font-semibold flex items-center ${deltaPct < 0 ? "text-emerald-500" : "text-rose-500"}`}>
                <TrendingDown className={`w-3 h-3 ${deltaPct > 0 ? "rotate-180" : ""}`} />
                {deltaPct > 0 ? "+" : ""}{deltaPct}
              </span>
            )}
          </div>
          <span className={`inline-block mt-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full ${sB.bg} ${sB.text}`}>
            {t[`band_${(simBand || "low").toLowerCase()}`] || simBand}
          </span>
        </div>
      </div>

      {bandChanged && (
        <div className={`text-[10px] mb-2 p-1.5 rounded-lg text-center font-semibold ${deltaPct < 0 ? "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" : "bg-rose-100 dark:bg-rose-500/15 text-rose-700 dark:text-rose-300"}`}>
          {t.transition}: {t[`band_${(realBand || "low").toLowerCase()}`]} → {t[`band_${(simBand || "low").toLowerCase()}`]}
        </div>
      )}

      {/* Sliders */}
      <ul className="space-y-2.5">
        {factors.map((f) => {
          const isHereditary = f.factor === "hereditary" || f.factor === "dna_repair";
          const cur = simBurdens[f.factor] ?? f.burden_0_1;
          const real = f.burden_0_1;
          return (
            <li key={f.factor}>
              <div className="flex items-center justify-between text-[10px] mb-0.5">
                <span className="font-semibold opacity-80">
                  {flabels[f.factor] || f.factor}
                </span>
                <span className="opacity-60 font-mono">
                  {(cur * 100).toFixed(0)}%
                  {Math.abs(cur - real) > 0.005 && (
                    <span className="opacity-50 ml-1">(real {(real * 100).toFixed(0)}%)</span>
                  )}
                  <span className="ml-1.5 opacity-40">· w={f.weight}</span>
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={cur}
                disabled={isHereditary}
                onChange={(e) => setBurden(f.factor, parseFloat(e.target.value))}
                className="w-full accent-violet-500 disabled:opacity-40"
                data-testid={`bdi-slider-${card.disease_id}-${f.factor}`}
              />
              {isHereditary && (
                <div className="text-[9px] opacity-50 italic mt-0.5">{t.hereditaryNote}</div>
              )}
            </li>
          );
        })}
      </ul>

      <div className="flex items-center justify-between gap-2 mt-3 pt-2 border-t border-violet-500/10">
        <details className="text-[10px] opacity-70">
          <summary className="cursor-pointer">{t.formulaTitle}</summary>
          <code className="block mt-1 font-mono text-[9px]">{t.formula}</code>
          <div className="text-[9px] opacity-60 mt-0.5">{t.note}</div>
        </details>
        <button
          type="button"
          onClick={reset}
          disabled={!isDirty}
          className={`text-[10px] inline-flex items-center gap-1 px-2 py-1 rounded-lg transition ${
            isDirty
              ? (isDark ? "bg-white/10 hover:bg-white/15 text-violet-300" : "bg-violet-100 hover:bg-violet-200 text-violet-700")
              : "opacity-40 cursor-not-allowed"
          }`}
          data-testid={`bdi-sensitivity-reset-${card.disease_id}`}
        >
          <RotateCcw className="w-3 h-3" />
          {t.reset}
        </button>
      </div>
    </div>
  );
}
